"use client";

import { useEffect, useState } from "react";
import { api, ApiError, DeadlinePreview, PurchaseCheckout, Slot } from "@/lib/api";
import { formatCountdown, formatDeadline, formatMoney, formatRelativeSlot } from "@/lib/format";
import { translateApiError } from "@/lib/errors";
import { Badge, Button, Modal } from "./ui";

type Step = "loading" | "hold" | "paying" | "confirmed" | "error";

export function BookingFlow({
  slot,
  trainerName,
  venueName,
  price,
  onClose,
  onBooked,
}: {
  slot: Slot;
  trainerName: string;
  venueName: string;
  price: string | null;
  onClose: () => void;
  onBooked: () => void;
}) {
  const [step, setStep] = useState<Step>("loading");
  const [checkout, setCheckout] = useState<PurchaseCheckout | null>(null);
  const [deadlines, setDeadlines] = useState<DeadlinePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.post<PurchaseCheckout>(`/slots/${slot.id}/purchase`),
      api.get<DeadlinePreview>(`/slots/${slot.id}/cancellation-preview`),
    ])
      .then(([checkoutRes, deadlinesRes]) => {
        if (cancelled) return;
        setCheckout(checkoutRes);
        setDeadlines(deadlinesRes);
        setStep("hold");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? String(e.detail) : "Не удалось удержать слот");
        setStep("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot.id]);

  useEffect(() => {
    if (step !== "hold") return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [step]);

  const msLeft = checkout ? new Date(checkout.hold_expires_at).getTime() - now : 0;
  const expired = step === "hold" && msLeft <= 0;

  const cancel = async () => {
    if (checkout) {
      await api.delete(`/holds/${checkout.hold_id}`).catch(() => {});
    }
    onClose();
  };

  const pay = async () => {
    if (!checkout) return;
    setStep("paying");
    setError(null);
    try {
      await api.post(`/payments/${checkout.provider_payment_id}/simulate-webhook`);
      setStep("confirmed");
    } catch (e) {
      setError(e instanceof ApiError ? String(e.detail) : "Оплата не прошла");
      setStep("hold");
    }
  };

  return (
    <Modal onClose={step === "confirmed" ? onBooked : cancel}>
      <div className="p-6">
        {(step === "loading" || step === "hold" || step === "paying") && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-text">Оформление записи</h2>
              <button onClick={cancel} className="text-text-muted hover:text-text" aria-label="Закрыть">
                ✕
              </button>
            </div>

            {step === "loading" && <p className="text-sm text-text-muted">Удерживаем слот…</p>}

            {checkout && !expired && (step === "hold" || step === "paying") && (
              <div className="mb-4 flex items-center justify-between rounded-lg bg-waiting/15 px-3 py-2">
                <span className="text-sm text-waiting-content dark:text-waiting">Слот держим за вами — не оплатите, вернётся в продажу</span>
                <span className="font-display text-lg font-bold text-waiting-content dark:text-waiting">{formatCountdown(msLeft)}</span>
              </div>
            )}
            {expired && (
              <p className="mb-4 rounded-lg bg-risk/10 px-3 py-2 text-sm text-risk">
                Время удержания истекло. Слот вернулся в продажу — выберите его заново.
              </p>
            )}

            <div className="mb-4 rounded-lg border border-border p-3 text-sm">
              <div className="font-medium text-text">{trainerName}</div>
              <div className="text-text-muted">{venueName}</div>
              <div className="text-text-muted">{formatRelativeSlot(slot.start_at)}</div>
            </div>

            <div className="mb-4 flex items-center justify-between text-sm">
              <span className="text-text-muted">К оплате</span>
              <span className="font-display text-xl font-bold text-text">{price ? formatMoney(price) : "—"}</span>
            </div>

            {deadlines && (
              <div className="mb-4 space-y-1.5 rounded-lg bg-border/30 p-3 text-xs text-text-muted">
                <div>Бесплатная отмена — до {formatDeadline(deadlines.cancel_deadline_at)}</div>
                <div>Перенос — до {formatDeadline(deadlines.reschedule_deadline_at)}</div>
                <div>После дедлайна занятие списывается полностью</div>
              </div>
            )}

            {error && <p className="mb-3 text-sm text-risk">{error}</p>}

            <Button onClick={pay} disabled={!checkout || expired || step === "paying"} className="w-full">
              {step === "paying" ? "Оплачиваем…" : `Перейти к оплате · ${price ? formatMoney(price) : ""}`}
            </Button>
          </>
        )}

        {step === "error" && (
          <>
            <h2 className="mb-3 font-display text-lg font-bold text-text">Не получилось</h2>
            <p className="mb-4 text-sm text-risk">{error}</p>
            <Button variant="secondary" onClick={onClose} className="w-full">
              Закрыть
            </Button>
          </>
        )}

        {step === "confirmed" && (
          <>
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-success/15 text-success-content dark:text-success">
                ✓
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-text">Запись подтверждена</h2>
                <Badge variant="success">оплачено {price ? formatMoney(price) : ""}</Badge>
              </div>
            </div>
            <div className="mb-4 rounded-lg border border-border p-3 text-sm">
              <div className="font-medium text-text">{formatRelativeSlot(slot.start_at)}</div>
              <div className="text-text-muted">
                {trainerName} · {venueName}
              </div>
            </div>
            <p className="mb-4 text-xs text-text-muted">
              Напоминания придут в Telegram: за сутки, за 3 часа и утром в день занятия.
            </p>
            <Button onClick={onBooked} className="w-full">
              Готово
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
